import { ProgramEngagementModel } from '@cibo/core'
import { Loading } from '@cibo/profile'
import {
  ConfirmIneligibleDialog,
  ConfirmIneligibleDialogProps,
  TaskDialog,
  useHandleLockDetails,
} from '@cibo/programs'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Button, ModalProps } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useProgramEngagementContext } from '../../features/ProgramEngagement'
import { ENGAGEMENT_ROUTES, EngagementRouteParams } from '../../navigation/types/engagementRoutes'
import { useNavigateToNextTask } from './useNavigateToNextTask'

export const TaskNavigation = () => {
  const { engagementId, stageId = 'apply', stepId, taskId } = useParams<EngagementRouteParams>()
  const { t } = useTranslation('@cibo/impact/TaskNavigation')
  const { basePath, engagement, ownerName, program, userRole } = useProgramEngagementContext()
  const isFirstTask =
    engagement.data?.getStepStatus(stepId)?.children.findIndex(({ id }) => id === taskId) === 0
  const taskContent = program.data?.taskContent(taskId)
  const navigate = useNavigate()
  const [taskDialogState, setTaskDialogState] = useState<
    Pick<ModalProps, 'open'> & Pick<EngagementRouteParams, 'taskId'>
  >({ open: false, taskId: undefined })

  const [confirmDialogState, setConfirmDialogState] = useState<
    Pick<ConfirmIneligibleDialogProps, 'open' | 'ineligibleReasons'>
  >({ open: false })

  const navigateNext = useNavigateToNextTask(setTaskDialogState)
  const handleLockDetails = useHandleLockDetails(
    engagement.data,
    program.data?.task(taskId),
    navigateNext
  )

  useEffect(() => {
    setTaskDialogState({ open: false, taskId: undefined })
    setConfirmDialogState({ open: false })
  }, [taskId])

  const onClickAdvance = async () => {
    const eligibilityReasons = engagement.data?.getTaskStatus(taskId)?.status.eligibility
      ?.reasonItems
    const progressReasons = engagement.data?.getTaskStatus(taskId)?.status.progress?.reasonItems

    //if problems, show confirmation dialog
    if (eligibilityReasons?.length || progressReasons?.length) {
      setConfirmDialogState({
        open: true,
        ineligibleReasons: [...(eligibilityReasons || []), ...(progressReasons || [])],
      })
    } else if (
      program.data?.task(taskId)?.rule.type === 'detailsLocked' &&
      !viewingTaskStatus?.status.progress?.completed
    ) {
      handleLockDetails()
    } else {
      !!taskId && navigateNext(taskId)
    }
  }

  //@todo this is a mess, refactor
  const onPrevious = useMemo(() => {
    if (!!program.data && !!stepId) {
      const step = program.data?.step(stepId)
      const currentTaskIndex = step?.children?.findIndex(({ id }) => id === taskId) ?? -1
      if (currentTaskIndex >= 0) {
        const prevTask = step?.children?.[currentTaskIndex - 1]
        if (prevTask) {
          return () =>
            navigate(
              generatePath(`${basePath}${ENGAGEMENT_ROUTES.TASK}`, {
                engagementId,
                stageId,
                stepId: step.id,
                taskId: prevTask?.id,
              })
            )
        }
      }

      if (!!stageId) {
        // else navigate to the next step
        const stage = program?.data.stage(stageId)
        const currentStepIndex = stage?.children.findIndex(({ id }) => id === stepId) ?? -1
        if (currentStepIndex >= 0) {
          const prevStep = stage?.children?.[currentStepIndex - 1]
          if (prevStep) {
            return () =>
              navigate(
                generatePath(`${basePath}${ENGAGEMENT_ROUTES.STEP}`, {
                  engagementId,
                  stageId,
                  stepId: prevStep?.id,
                  taskId: prevStep?.children?.[0]?.id,
                })
              )
          }
        }
      }
    }
  }, [basePath, stepId, taskId, engagement.dataUpdatedAt, program.dataUpdatedAt])

  const onConfirmIneligible = useCallback(
    (updatedEngagement?: ProgramEngagementModel) => {
      setConfirmDialogState({ open: false })

      if (!updatedEngagement) {
        navigate(basePath)
      } else if (updatedEngagement?.getTaskStatus(taskId)?.status.eligibility?.reasonItems.length) {
        navigate(
          generatePath(`${basePath}${ENGAGEMENT_ROUTES.STEP_STATUS}`, {
            engagementId,
            stageId,
            stepId,
          })
        )
      } else {
        !!taskId && navigateNext(taskId)
      }
    },
    [engagement.dataUpdatedAt, engagementId, taskId, navigate]
  )

  if (program.isLoading) {
    return <Loading />
  }

  const viewingTaskStatus = engagement.data?.getTaskStatus(taskId)

  return (
    <>
      {!isFirstTask ? (
        <Button onClick={onPrevious} startIcon={<ChevronLeft />}>
          {t('previous')}
        </Button>
      ) : (
        <div />
      )}
      <Button
        data-testid="next-task-button"
        onClick={() => onClickAdvance()}
        endIcon={!taskContent?.content.cta && <ChevronRight />}
        variant={taskContent?.content.cta ? 'contained' : undefined}
        disabled={
          !viewingTaskStatus?.allowedActions?.includes('canModify') ||
          !taskId ||
          !viewingTaskStatus?.status.progress?.completed
        }
      >
        {(viewingTaskStatus?.status.progress?.completed
          ? taskContent?.content.completedCta
          : undefined) ??
          taskContent?.content.cta ??
          t('next')}
      </Button>

      <ConfirmIneligibleDialog
        engagementId={engagementId}
        onClose={() => setConfirmDialogState({ open: false })}
        onConfirm={onConfirmIneligible}
        {...confirmDialogState}
      />

      <TaskDialog
        {...taskDialogState}
        engagement={engagement.data}
        onClose={() => setTaskDialogState({ open: false })}
        onComplete={navigateNext}
        ownerName={ownerName}
        taskId={taskDialogState.taskId}
        userRole={userRole}
      />
    </>
  )
}
